<template>
  <section class="product-gallery-editor">
    <label class="form-label fw-medium">Fotos do produto</label>
    <p>Adicione até 6 fotos. A primeira será a capa do catálogo.</p>
    <div v-if="modelValue.length" class="product-gallery-editor__items">
      <div v-for="(image, index) in modelValue" :key="image.id" class="product-gallery-editor__item">
        <img :src="image.url" :alt="`Foto ${index + 1} do produto`" />
        <span v-if="index === 0">Capa</span>
        <div>
          <button type="button" :disabled="disabled || index === 0" aria-label="Mover foto para antes" @click="move(index, -1)">←</button>
          <button type="button" :disabled="disabled || index === modelValue.length - 1" aria-label="Mover foto para depois" @click="move(index, 1)">→</button>
          <button type="button" :disabled="disabled" aria-label="Remover foto" @click="remove(index)">×</button>
        </div>
      </div>
    </div>
    <label v-if="modelValue.length < 6" class="product-gallery-editor__add">
      <i class="ph ph-camera" aria-hidden="true"></i> {{ busy ? 'Enviando fotos…' : 'Adicionar fotos' }}
      <input type="file" multiple accept="image/jpeg,image/png,image/webp" :disabled="disabled || busy" @change="upload" />
    </label>
    <small>JPEG, PNG ou WebP, até 5 MB por foto.</small>
    <p v-if="error" class="text-danger" role="alert">{{ error }}</p>
  </section>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref } from 'vue';
import { catalogApi, catalogError } from '@/services/catalog';
const props = defineProps({ modelValue: { type: Array, default: () => [] }, disabled: Boolean });
const emit = defineEmits(['update:modelValue', 'busy']);
const busy = ref(false);
const error = ref('');
function move(index, offset) {
  const images = [...props.modelValue];
  [images[index], images[index + offset]] = [images[index + offset], images[index]];
  emit('update:modelValue', images);
}
function remove(index) { emit('update:modelValue', props.modelValue.filter((_, i) => i !== index)); }
async function upload(event) {
  const files = [...(event.target.files || [])];
  event.target.value = '';
  error.value = '';
  if (files.length + props.modelValue.length > 6) { error.value = 'Cada produto pode ter até 6 fotos.'; return; }
  if (files.some(file => !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024)) {
    error.value = 'Escolha fotos JPEG, PNG ou WebP de até 5 MB cada.'; return;
  }
  busy.value = true;
  emit('busy', true);
  try {
    for (const file of files) {
      const image = await catalogApi.upload(file, 'product');
      emit('update:modelValue', [...props.modelValue, image]);
      // Allow v-model to update before the next file completes.
      await Promise.resolve();
    }
  } catch (e) { error.value = catalogError(e); }
  finally { busy.value = false; emit('busy', false); }
}
</script>

<style scoped>
.product-gallery-editor { margin: 24px 0 12px; }
.product-gallery-editor p, .product-gallery-editor small { color: #758293; font-size: 12px; margin: 0 0 12px; }
.product-gallery-editor__items { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 12px; }
.product-gallery-editor__item { min-width: 0; position: relative; border: 1px solid #dce5e9; border-radius: 10px; padding: 6px; }
.product-gallery-editor__item img { width: 100%; height: 90px; object-fit: contain; }
.product-gallery-editor__item > span { position: absolute; left: 5px; top: 5px; background: #e6f7ef; color: #187356; padding: 2px 6px; border-radius: 5px; font-size: 10px; }
.product-gallery-editor__item > div { display: flex; justify-content: center; gap: 5px; }
.product-gallery-editor__item button { background: #f2f5f6; border: 0; border-radius: 5px; color: #344858; width: 28px; height: 26px; }
.product-gallery-editor__item button:disabled { opacity: .3; }
.product-gallery-editor__add { padding: 12px; border: 1px dashed #a9cbbd; color: #177758; display: flex; justify-content: center; align-items: center; gap: 8px; border-radius: 9px; cursor: pointer; margin-bottom: 6px; }
.product-gallery-editor__add input { display: none; }
</style>
